import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Lock, Sparkles, Clock, Zap } from 'lucide-react';
import { Card, CardContent } from './card';
import { Button } from './button';
import { Badge } from './badge';
import { useSchedulingFeature } from '../../context/SchedulingFeatureContext';
import { useTheme } from '../../context/ThemeContext';

// Value propositions for scheduling
const VALUE_CHIPS = [
  { icon: Clock, text: 'Save time' },
  { icon: Calendar, text: 'Plan content' },
  { icon: Zap, text: 'Auto-publish' }
];

/**
 * Upgrade card shown when user doesn't have scheduling access
 * Variants: 'card' (dashboard), 'inline' (upload modal), 'banner' (settings)
 */
export const SchedulingUpgradeCard = ({ 
  variant = 'card', 
  placement = 'unknown',
  onUpgradeClick,
  className = ''
}) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { 
    SCHEDULING_TIER_NAME, 
    trackUpgradeViewed, 
    trackUpgradeClicked 
  } = useSchedulingFeature();

  // Track view on mount
  useEffect(() => {
    trackUpgradeViewed(placement);
  }, [trackUpgradeViewed, placement]);

  const handleUpgradeClick = () => {
    trackUpgradeClicked(placement);
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      // Navigate to pricing with preselection
      navigate('/pricing?highlight=on_ya_royalties&feature=scheduling');
    }
  };

  // Inline variant for upload modal
  if (variant === 'inline') {
    return (
      <div 
        className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'} ${className}`}
        role="region"
        aria-label="Post scheduling upgrade prompt"
      >
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
            <Lock className="w-5 h-5 text-yellow-500" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Post scheduling is available in {SCHEDULING_TIER_NAME}.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {VALUE_CHIPS.map((chip, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className={`text-xs ${isDark ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-600'}`}
                >
                  <chip.icon className="w-3 h-3 mr-1" aria-hidden="true" />
                  {chip.text}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <Button
          onClick={handleUpgradeClick}
          className="w-full mt-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-semibold min-h-[44px]"
          aria-label={`Upgrade to ${SCHEDULING_TIER_NAME} to unlock post scheduling`}
        >
          Upgrade to {SCHEDULING_TIER_NAME}
        </Button>
      </div>
    );
  }

  // Banner variant for settings
  if (variant === 'banner') {
    return (
      <div 
        className={`p-4 rounded-lg border-2 border-dashed ${isDark ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-yellow-400/50 bg-yellow-50'} ${className}`}
        role="region"
        aria-label="Post scheduling upgrade prompt"
        aria-live="polite"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-yellow-500" aria-hidden="true" />
            <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
              Upgrade to {SCHEDULING_TIER_NAME} to schedule posts.
            </span>
          </div>
          <Button
            onClick={handleUpgradeClick}
            size="sm"
            className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-semibold min-h-[44px] min-w-[44px]"
            aria-label={`Upgrade to ${SCHEDULING_TIER_NAME}`}
          >
            Upgrade to {SCHEDULING_TIER_NAME}
          </Button>
        </div>
      </div>
    );
  }

  // Default card variant for dashboard
  return (
    <Card 
      className={`${isDark ? 'bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30' : 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200'} ${className}`}
      role="region"
      aria-label="Unlock post scheduling upgrade card"
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
            <Calendar className="w-6 h-6 text-yellow-500" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Unlock Post Scheduling
            </h3>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Schedule posts to publish automatically with {SCHEDULING_TIER_NAME}.
            </p>
            
            {/* Value chips */}
            <div className="flex flex-wrap gap-2 mt-3">
              {VALUE_CHIPS.map((chip, idx) => (
                <Badge 
                  key={idx} 
                  className={`${isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'} text-xs`}
                >
                  <chip.icon className="w-3 h-3 mr-1" aria-hidden="true" />
                  {chip.text}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        
        <Button
          onClick={handleUpgradeClick}
          className="w-full mt-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-semibold min-h-[44px]"
          aria-label={`Upgrade to ${SCHEDULING_TIER_NAME} to unlock post scheduling`}
        >
          <Sparkles className="w-4 h-4 mr-2" aria-hidden="true" />
          Upgrade to {SCHEDULING_TIER_NAME}
        </Button>
      </CardContent>
    </Card>
  );
};

/**
 * Discovery card shown to users who have scheduling access but haven't used it yet
 */
export const SchedulingDiscoveryCard = ({ 
  variant = 'card',
  placement = 'unknown',
  onDismiss,
  className = ''
}) => {
  const { isDark } = useTheme();
  const { dismissDiscovery, trackAnalytics } = useSchedulingFeature();

  useEffect(() => {
    trackAnalytics('scheduling_discovery_viewed', { placement });
  }, [trackAnalytics, placement]);

  const handleDismiss = () => {
    dismissDiscovery();
    if (onDismiss) onDismiss();
  };

  if (variant === 'tooltip') {
    return (
      <div 
        className={`p-3 rounded-lg ${isDark ? 'bg-green-500/20 border border-green-500/30' : 'bg-green-50 border border-green-200'} ${className}`}
        role="tooltip"
        aria-live="polite"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-green-500" aria-hidden="true" />
          <span className={`text-sm ${isDark ? 'text-green-300' : 'text-green-700'}`}>
            You can schedule this post for later!
          </span>
        </div>
      </div>
    );
  }

  return (
    <Card 
      className={`${isDark ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30' : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'} ${className}`}
      role="region"
      aria-label="Post scheduling feature discovery"
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
              <Calendar className="w-5 h-5 text-green-500" aria-hidden="true" />
            </div>
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Schedule your posts!
              </p>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Your {SCHEDULING_TIER_NAME} tier includes post scheduling.
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className={`text-xs ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} min-w-[44px] min-h-[44px] flex items-center justify-center`}
            aria-label="Dismiss scheduling discovery tip"
          >
            Dismiss
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

// Helper text for locked scheduling in various places
export const SchedulingLockedText = ({ className = '' }) => {
  const { isDark } = useTheme();
  const { SCHEDULING_TIER_NAME } = useSchedulingFeature();
  
  return (
    <p 
      className={`text-sm flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'} ${className}`}
      role="status"
      aria-live="polite"
    >
      <Lock className="w-4 h-4" aria-hidden="true" />
      Post scheduling available in {SCHEDULING_TIER_NAME}
    </p>
  );
};

const SCHEDULING_TIER_NAME = 'Own Ya Royalties';

export default SchedulingUpgradeCard;
